import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Comment, ApiCommentWithReplies, ApiCommentResponse } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/apiService';

interface CommentsContextType {
  comments: Record<string, Comment[]>; // portfolioId -> comments array
  isLoading: Record<string, boolean>; // portfolioId -> loading state
  fetchComments: (portfolioId: string) => Promise<void>;
  fetchCommentCount: (portfolioId: string) => Promise<void>; // Fetch just the count
  fetchCommentCountsBatch: (portfolioIds: string[]) => Promise<void>; // Batch fetch counts
  addComment: (portfolioId: string, content: string, category: string, tags?: string[]) => Promise<void>;
  addReply: (portfolioId: string, parentId: string, content: string, tags?: string[]) => Promise<void>;
  getCommentsForPortfolio: (portfolioId: string) => Comment[];
  getCommentCount: (portfolioId: string) => number;
}

const CommentsContext = createContext<CommentsContextType | undefined>(undefined);

interface CommentsProviderProps {
  children: ReactNode;
}

// Helper function to convert API comment to frontend Comment type
const convertApiComment = (apiComment: ApiCommentResponse): Comment => {
  return {
    id: apiComment.comment_id,
    portfolioId: apiComment.portfolio_id_lig,
    userId: apiComment.user_id,
    userName: apiComment.user_name,
    userRole: apiComment.user_role,
    content: apiComment.content,
    category: apiComment.category,
    tags: apiComment.tags || undefined,
    timestamp: new Date(apiComment.created_at),
    parentId: apiComment.parent_id || undefined,
    replies: []
  };
};

export const CommentsProvider: React.FC<CommentsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({}); // Separate count storage
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});

  // Fetch just the comment count (lightweight)
  const fetchCommentCount = useCallback(async (portfolioId: string) => {
    if (!user) return;

    try {
      const response = await apiService.getCommentCount(portfolioId);
      setCommentCounts(prev => ({
        ...prev,
        [portfolioId]: response.count
      }));
    } catch (error) {
      console.error('Error fetching comment count:', error);
      // Set 0 on error
      setCommentCounts(prev => ({
        ...prev,
        [portfolioId]: 0
      }));
    }
  }, [user]);

  // Batch fetch comment counts for multiple portfolios (much more efficient)
  const fetchCommentCountsBatch = useCallback(async (portfolioIds: string[]) => {
    if (!user || portfolioIds.length === 0) return;

    try {
      const response = await apiService.getCommentCountsBatch(portfolioIds);

      // Update all counts at once
      const newCounts: Record<string, number> = {};
      response.forEach(item => {
        newCounts[item.portfolio_id] = item.count;
      });

      // Also set 0 for any portfolios not in the response (no comments)
      portfolioIds.forEach(pid => {
        if (newCounts[pid] === undefined) {
          newCounts[pid] = 0;
        }
      });

      setCommentCounts(prev => ({
        ...prev,
        ...newCounts
      }));
    } catch (error) {
      console.error('Error fetching comment counts batch:', error);
      // Set 0 for all on error
      const zeroCounts: Record<string, number> = {};
      portfolioIds.forEach(pid => {
        zeroCounts[pid] = 0;
      });
      setCommentCounts(prev => ({
        ...prev,
        ...zeroCounts
      }));
    }
  }, [user]);

  // Fetch comments from API
  const fetchComments = useCallback(async (portfolioId: string) => {
    if (!user) return;

    setIsLoading(prev => ({ ...prev, [portfolioId]: true }));
    
    try {
      const response = await apiService.getPortfolioComments(portfolioId);
      
      // Convert API comments to frontend Comment type
      const allComments: Comment[] = [];
      
      response.comments.forEach((topLevelComment: ApiCommentWithReplies) => {
        // Add top-level comment
        const comment = convertApiComment(topLevelComment);
        allComments.push(comment);
        
        // Add replies
        topLevelComment.replies.forEach((reply: ApiCommentResponse) => {
          const replyComment = convertApiComment(reply);
          allComments.push(replyComment);
        });
      });
      
      setComments(prev => ({
        ...prev,
        [portfolioId]: allComments
      }));
      
      // Also update the count
      setCommentCounts(prev => ({
        ...prev,
        [portfolioId]: allComments.length
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      // Set empty array on error
      setComments(prev => ({
        ...prev,
        [portfolioId]: []
      }));
      setCommentCounts(prev => ({
        ...prev,
        [portfolioId]: 0
      }));
    } finally {
      setIsLoading(prev => ({ ...prev, [portfolioId]: false }));
    }
  }, [user]);

  // Add a new top-level comment
  const addComment = useCallback(async (
    portfolioId: string, 
    content: string, 
    category: string,
    tags?: string[]
  ) => {
    if (!user) {
      throw new Error('User must be authenticated to add comments');
    }

    try {
      await apiService.createComment(portfolioId, {
        content,
        category,
        tags
      });
      
      // Refresh comments after creating
      await fetchComments(portfolioId);
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  }, [user, fetchComments]);

  // Add a reply to a comment
  const addReply = useCallback(async (
    portfolioId: string, 
    parentId: string, 
    content: string,
    tags?: string[]
  ) => {
    if (!user) {
      throw new Error('User must be authenticated to add replies');
    }

    try {
      await apiService.createReply(portfolioId, parentId, {
        content,
        tags
      });
      
      // Refresh comments after creating reply
      await fetchComments(portfolioId);
    } catch (error) {
      console.error('Error creating reply:', error);
      throw error;
    }
  }, [user, fetchComments]);

  // Get comments for a specific portfolio
  const getCommentsForPortfolio = useCallback((portfolioId: string): Comment[] => {
    return comments[portfolioId] || [];
  }, [comments]);

  // Get comment count for a specific portfolio
  // Returns from count cache if available, otherwise from full comments
  const getCommentCount = useCallback((portfolioId: string): number => {
    // Prefer the count cache (more up-to-date if fetched separately)
    if (commentCounts[portfolioId] !== undefined) {
      return commentCounts[portfolioId];
    }
    // Fall back to counting full comments if available
    return (comments[portfolioId] || []).length;
  }, [comments, commentCounts]);

  const value: CommentsContextType = {
    comments,
    isLoading,
    fetchComments,
    fetchCommentCount,
    fetchCommentCountsBatch,
    addComment,
    addReply,
    getCommentsForPortfolio,
    getCommentCount
  };

  return (
    <CommentsContext.Provider value={value}>
      {children}
    </CommentsContext.Provider>
  );
};

export const useComments = (): CommentsContextType => {
  const context = useContext(CommentsContext);
  if (!context) {
    throw new Error('useComments must be used within a CommentsProvider');
  }
  return context;
};
